import { useState, useEffect } from 'react'
import { videoService } from '../../services'
import { useAuthStore } from '../../stores'
import { Button, Input, Spinner } from '../common'
import { VideoRecorder } from '../video'
import type { Video } from '../../types/database.types'

interface VideoSelectorProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (video: Video) => void
  excludeVideoIds?: string[]
}

export function VideoSelector({
  isOpen,
  onClose,
  onSelect,
  excludeVideoIds = [],
}: VideoSelectorProps) {
  const { profile } = useAuthStore()
  const [videos, setVideos] = useState<Video[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showRecorder, setShowRecorder] = useState(false)

  useEffect(() => {
    if (isOpen && profile?.id) {
      loadVideos()
    }
  }, [isOpen, profile?.id])

  const loadVideos = async () => {
    if (!profile?.id) return
    setIsLoading(true)
    try {
      const result = await videoService.getMyVideos(profile.id)
      setVideos(result)
    } catch (error) {
      console.error('Failed to load videos:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleRecordingComplete = async (videoId: string) => {
    setShowRecorder(false)
    // Fetch the newly recorded video and select it
    try {
      const video = await videoService.getVideoById(videoId)
      if (video) {
        onSelect(video)
        onClose()
      }
    } catch (error) {
      console.error('Failed to fetch recorded video:', error)
      onClose()
    }
  }

  if (!isOpen) return null

  const filteredVideos = videos.filter((video) => {
    if (excludeVideoIds.includes(video.id)) return false
    if (searchQuery) {
      return video.title.toLowerCase().includes(searchQuery.toLowerCase())
    }
    return true
  })

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '--:--'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getThumbnailUrl = (video: Video): string | null => {
    if (video.thumbnail_url) return video.thumbnail_url
    if (video.mux_playback_id) {
      return `https://image.mux.com/${video.mux_playback_id}/thumbnail.png?width=160&height=96&fit_mode=smartcrop`
    }
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Add Video to Program
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="mt-4">
            <Input
              placeholder="Search videos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Video list */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner />
            </div>
          ) : filteredVideos.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {searchQuery ? 'No videos match your search' : 'No videos available'}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredVideos.map((video) => (
                <button
                  key={video.id}
                  onClick={() => {
                    onSelect(video)
                    onClose()
                  }}
                  className="w-full flex items-center gap-4 p-3 rounded-lg border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
                >
                  <div className="w-20 h-12 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                    {getThumbnailUrl(video) ? (
                      <img
                        src={getThumbnailUrl(video)!}
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg
                          className="w-6 h-6 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate">
                      {video.title}
                    </h4>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span>{formatDuration(video.duration_seconds)}</span>
                      {video.category && (
                        <>
                          <span>·</span>
                          <span>{video.category}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={() => setShowRecorder(true)}
              className="flex-1 flex items-center justify-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <circle cx="12" cy="12" r="10" strokeWidth={2} />
                <circle cx="12" cy="12" r="4" fill="currentColor" />
              </svg>
              Record New
            </Button>
          </div>
        </div>
      </div>

      {/* Video Recorder Modal */}
      <VideoRecorder
        isOpen={showRecorder}
        onClose={() => setShowRecorder(false)}
        onUploadComplete={handleRecordingComplete}
      />
    </div>
  )
}
